<template>
  <div class="szw-card">
    <!-- Top: workspace / owner tag + more menu -->
    <div class="szw-card__top">
      <span v-if="contact.owner_actor_name" class="szw-card__workspace" :title="contact.owner_actor_name">
        <span class="material-symbols-outlined">group</span>
        {{ contact.owner_actor_name }}
      </span>
      <span v-else></span>
      <button class="szw-card__more" :aria-label="$t('samergo_my_network_transfer_title')" @click.stop="toggleMenu($event)">
        <span class="material-symbols-outlined">more_horiz</span>
      </button>
    </div>

    <!-- Main: logo + name + legend pill -->
    <div class="szw-card__main">
      <div class="szw-card__logo">
        <ActorAvatar :actor="contact" :width="40" variant="szw-logo" prefer="screenshot"/>
      </div>
      <div class="szw-card__name-block">
        <p class="szw-card__name szw-card__name--link" @click="openActor">{{ contact.name || $t('samergo_my_network_unknown') }}</p>
        <span v-if="legendLabel" class="szw-card__pill">{{ legendLabel }}</span>
      </div>
    </div>

    <!-- More menu (teleported dropdown anchored to the 3-dots button) -->
    <Teleport to="body">
      <div
        v-if="menuOpen && menuPos"
        class="szw-menu"
        :style="{ top: menuPos.top + 'px', right: menuPos.right + 'px' }"
        @click.stop
      >
        <button class="szw-menu__item" @click="openActor">
          <span class="material-symbols-outlined">open_in_new</span>{{ $t('samergo_my_network_view_profile') }}
        </button>
        <button v-if="myClaims.length" class="szw-menu__item" @click.stop="openTransfer($event)">
          <span class="material-symbols-outlined">swap_horiz</span>{{ $t('samergo_my_network_transfer_title') }}
        </button>
        <button class="szw-menu__item szw-menu__item--danger" @click="removeContact">
          <span class="material-symbols-outlined">delete</span>{{ $t('samergo_network_remove_from_wijk') }}
        </button>
      </div>
    </Teleport>

    <!-- Localized description (when present and no inline editing focus) -->
    <p v-if="localizedDescription" class="szw-card__desc">{{ localizedDescription }}</p>

    <!-- Details toggle -->
    <button class="szw-card__details-btn" @click="detailsOpen = !detailsOpen">
      <span>{{ $t('samergo_my_network_add_contact_details') }}</span>
      <span class="material-symbols-outlined szw-card__chevron" :class="{ 'szw-card__chevron--open': detailsOpen }">expand_more</span>
    </button>

    <!-- Details: phone + note inputs, then employees -->
    <div v-if="detailsOpen" class="szw-card__details">
      <div class="szw-card__inputs">
        <input
          v-model="phoneDraft"
          class="szw-card__input"
          type="tel"
          :placeholder="$t('samergo_my_network_phone_input_placeholder')"
          @blur="saveFields"
          @keyup.enter="saveFields"
        />
        <input
          v-model="noteDraft"
          class="szw-card__input"
          type="text"
          :placeholder="$t('samergo_my_network_notes_add')"
          @blur="saveFields"
          @keyup.enter="saveFields"
        />
      </div>

      <!-- Employees / contributors -->
      <button class="szw-card__employees-row" @click="toggleContributors">
        <span class="tw-flex items-center gap-1.5">
          <span class="material-symbols-outlined szw-card__employees-icon">group</span>
          {{ $t('samergo_my_network_contributors') }}
          <span v-if="contributors && contributors.length" class="szw-card__employees-count">{{ contributors.length }}</span>
        </span>
        <span class="material-symbols-outlined szw-card__chevron" :class="{ 'szw-card__chevron--open': contributorsOpen }">expand_more</span>
      </button>

      <div v-if="contributorsOpen" class="szw-card__contributors">
        <!-- Loading -->
        <template v-if="contributorsLoading">
          <div v-for="n in 2" :key="n" class="szw-card__contributor szw-card__contributor--skeleton">
            <div class="szw-card__contributor-avatar shimmer-contributor"></div>
            <div style="flex: 1;">
              <div class="shimmer-contributor" style="height: 11px; width: 55%; border-radius: 4px;"></div>
              <div class="shimmer-contributor" style="height: 9px; width: 35%; border-radius: 4px; margin-top: 5px;"></div>
            </div>
          </div>
        </template>

        <!-- Empty -->
        <p v-else-if="!contributors || !contributors.length" class="szw-card__contributors-empty">
          {{ $t('samergo_my_network_no_contributors') }}
        </p>

        <!-- Contributor rows -->
        <template v-else>
          <div v-for="contributor in contributors" :key="contributor.user_id" class="szw-card__contributor">
            <div class="szw-card__contributor-avatar">
              <img v-if="contributor.photo_url" :src="contributor.photo_url" :alt="contributor.user_name" class="szw-card__contributor-avatar-img"/>
              <span v-else class="material-symbols-outlined" style="font-size: 16px; color: #071c5b;">person</span>
            </div>
            <div class="szw-card__contributor-body">
              <p class="szw-card__contributor-name">{{ contributor.user_name }}</p>
              <div class="szw-card__inputs">
                <input
                  v-if="contributorDrafts[contributor.user_id]"
                  v-model="contributorDrafts[contributor.user_id].phone"
                  class="szw-card__input szw-card__input--sm"
                  type="tel"
                  :placeholder="$t('samergo_my_network_contributor_phone_placeholder')"
                  @blur="saveContributor(contributor.user_id)"
                  @keyup.enter="saveContributor(contributor.user_id)"
                />
                <input
                  v-if="contributorDrafts[contributor.user_id]"
                  v-model="contributorDrafts[contributor.user_id].notes"
                  class="szw-card__input szw-card__input--sm"
                  type="text"
                  :placeholder="$t('samergo_my_network_contributor_notes_add')"
                  @blur="saveContributor(contributor.user_id)"
                  @keyup.enter="saveContributor(contributor.user_id)"
                />
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Transfer / copy picker (teleported to escape overflow) -->
    <Teleport to="body">
      <div
        v-if="transferOpen && transferPos"
        class="szw-popup"
        :style="{ top: transferPos.top + 'px', right: transferPos.right + 'px' }"
        @click.stop
      >
        <div class="szw-popup__header">
          <span class="material-symbols-outlined szw-popup__header-icon">swap_horiz</span>
          <span class="szw-popup__header-label">{{ $t('samergo_my_network_transfer_title') }}</span>
        </div>
        <div class="szw-popup__options">
          <template v-if="myClaims.filter(c => c.actor_id !== contact.owner_actor_id).length">
            <p class="szw-popup__section-label">{{ $t('samergo_transfer_move_to') }}</p>
            <template v-for="claim in myClaims" :key="'move-' + claim.actor_id">
              <button
                v-if="claim.actor_id !== contact.owner_actor_id"
                class="szw-popup__option"
                @click="transferContact(claim.actor_id, false)"
              >
                <ActorAvatar :actor="claim" :width="32" variant="rounded" class="flex-shrink-0"/>
                <span class="szw-popup__option-name">{{ claim.name }}</span>
              </button>
            </template>
            <hr class="szw-popup__divider"/>
            <p class="szw-popup__section-label">{{ $t('samergo_transfer_copy_to') }}</p>
            <template v-for="claim in myClaims" :key="'copy-' + claim.actor_id">
              <button
                v-if="claim.actor_id !== contact.owner_actor_id"
                class="szw-popup__option"
                @click="transferContact(claim.actor_id, true)"
              >
                <ActorAvatar :actor="claim" :width="32" variant="rounded" class="flex-shrink-0"/>
                <span class="szw-popup__option-name">{{ claim.name }}</span>
                <span class="material-symbols-outlined szw-popup__copy-icon">content_copy</span>
              </button>
            </template>
          </template>
          <p v-else class="szw-popup__empty">{{ $t('samergo_transfer_no_options') }}</p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { ACTION_TYPES } from '@/store/modules/network-contacts.js'
  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'
  import { fetchContributors } from '@/api/actors'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import { stripHtmlTags } from '@/util/helpers'

  export default defineComponent({
    name: 'SamergoNetworkCard',
    components: { ActorAvatar },
    mixins: [TranslationsMixin],
    emits: ['removed'],
    props: {
      contact: { type: Object, required: true },
      myClaims: { type: Array, default: () => [] },
    },
    data() {
      return {
        menuOpen: false,
        menuPos: null as { top: number; right: number } | null,
        detailsOpen: false,
        contributorsOpen: false,
        contributors: null as any[] | null,
        contributorsLoading: false,
        transferOpen: false,
        transferPos: null as { top: number; right: number } | null,
        phoneDraft: '',
        noteDraft: '',
        contributorDrafts: {} as Record<number, { phone: string; notes: string }>,
      }
    },
    created() {
      this.phoneDraft = this.contact.phone || ''
      this.noteDraft = this.contact.notes || ''
    },
    mounted() {
      document.addEventListener('click', this.closePopups)
    },
    beforeUnmount() {
      document.removeEventListener('click', this.closePopups)
    },
    watch: {
      'contact.phone'(value: string) {
        this.phoneDraft = value || ''
      },
      'contact.notes'(value: string) {
        this.noteDraft = value || ''
      },
    },
    computed: {
      legendLabel(): string {
        // Tags represent the industries taxonomy. Prefer the actor's first industry id,
        // and fall back to the computed legend id. Optional chaining + nullish coalescing
        // make this safe when `industries` is absent or empty. `this.locale` keeps it
        // reactive to language switches. Returns '' (pill hidden) when nothing resolves —
        // never a raw id.
        const industryId = this.contact?.industries?.[0] ?? this.contact?.legend
        if (industryId == null) return ''
        return this.getLabelForTaxonomyValue('industries', industryId, this.locale) || ''
      },
      localizedDescription(): string {
        // Use the per-locale actor translation when present, falling back to the default
        // (already plain-text) description. Strip tags in case a translation carries HTML.
        const value = this.localizedDisplayProperty(this.contact, 'description') || ''
        return stripHtmlTags(value)
      },
    },
    methods: {
      toggleMenu(event: MouseEvent) {
        if (this.menuOpen) {
          this.menuOpen = false
          return
        }
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
        this.menuPos = { top: rect.bottom + 6, right: window.innerWidth - rect.right }
        this.menuOpen = true
        this.transferOpen = false
      },
      closePopups() {
        this.menuOpen = false
        this.transferOpen = false
      },
      openActor() {
        this.$router.push('/actors/' + this.contact.elastic_id)
      },
      saveFields() {
        if (this.phoneDraft === (this.contact.phone || '') && this.noteDraft === (this.contact.notes || '')) {
          return
        }
        this.$store.dispatch(ACTION_TYPES.UPDATE_CONTACT, {
          elasticId: this.contact.elastic_id,
          phone: this.phoneDraft,
          notes: this.noteDraft,
        })
      },
      async loadContributors() {
        if (this.contributors !== null) return
        this.contributorsLoading = true
        try {
          const res = await fetchContributors(this.contact.elastic_id, { includeOwners: true })
          this.contributors = Array.isArray(res) ? res : []
        } catch {
          this.contributors = []
        } finally {
          this.contributorsLoading = false
          // Seed editable drafts for each contributor from the saved contributor data
          const drafts: Record<number, { phone: string; notes: string }> = {}
          ;(this.contributors ?? []).forEach((c: any) => {
            const data = (this.contact.contributors_data ?? {})[c.user_id] ?? { phone: '', notes: '' }
            drafts[c.user_id] = { phone: data.phone || '', notes: data.notes || '' }
          })
          this.contributorDrafts = drafts
        }
      },
      toggleContributors() {
        if (!this.contributorsOpen && this.contributors === null) {
          this.loadContributors()
        }
        this.contributorsOpen = !this.contributorsOpen
      },
      saveContributor(userId: number) {
        const draft = this.contributorDrafts[userId]
        if (!draft) return
        const existing = this.contact.contributors_data ?? {}
        const current = existing[userId] ?? { phone: '', notes: '' }
        if (draft.phone === (current.phone || '') && draft.notes === (current.notes || '')) {
          return
        }
        this.$store.dispatch(ACTION_TYPES.UPDATE_CONTACT, {
          elasticId: this.contact.elastic_id,
          phone: this.contact.phone,
          notes: this.contact.notes,
          contributors_data: { ...existing, [userId]: { phone: draft.phone, notes: draft.notes } },
        })
      },
      removeContact() {
        this.menuOpen = false
        this.$store.dispatch(ACTION_TYPES.REMOVE_CONTACT, this.contact.id)
        this.$emit('removed', this.contact.id)
      },
      openTransfer(event: MouseEvent) {
        this.menuOpen = false
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
        this.transferPos = { top: rect.bottom + 8, right: window.innerWidth - rect.right }
        this.transferOpen = true
      },
      async transferContact(ownerActorId: number | null, duplicate: boolean) {
        this.transferOpen = false
        await this.$store.dispatch(ACTION_TYPES.TRANSFER_CONTACT, {
          elasticId: this.contact.elastic_id,
          ownerActorId,
          duplicate,
        })
      },
    },
  })
</script>

<style scoped lang="scss">
  $szw-navy: #071c5b;
  $szw-green: #006e29;
  $szw-surface-low: #f4f3ef;
  $szw-surface-lower: #ecebe6;
  $szw-border: rgba(0, 0, 0, 0.12);
  $szw-text: #1c1c1a;
  $szw-text-secondary: #5f5e5a;
  $szw-text-tertiary: #888780;

  .szw-card {
    position: relative;
    background: #fff;
    border-radius: 12px;
    border: 0.5px solid $szw-border;
    padding: 1rem 1.25rem;
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
    transition: box-shadow 0.2s, transform 0.15s;

    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.07);
    }
  }

  .szw-card__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    min-height: 24px;
  }

  .szw-card__workspace {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    color: $szw-text-tertiary;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    .material-symbols-outlined {
      font-size: 14px;
    }
  }

  .szw-card__more {
    border: none;
    background: none;
    padding: 4px;
    color: $szw-text-tertiary;
    cursor: pointer;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:hover {
      background: $szw-surface-low;
      color: $szw-text;
    }

    .material-symbols-outlined {
      font-size: 18px;
    }
  }

  .szw-card__main {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .szw-card__logo {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1.5px solid $szw-green;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;

    :deep(> div) {
      width: 100% !important;
      min-width: 100% !important;
      height: 100% !important;
      max-height: 100% !important;
    }

    :deep(img) {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 50%;
    }
  }

  .szw-card__name-block {
    flex: 1;
    min-width: 0;
  }

  .szw-card__name {
    font-weight: 600;
    font-size: 15px;
    margin: 0;
    color: $szw-text;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .szw-card__name--link {
    cursor: pointer;

    &:hover {
      color: $szw-navy;
      text-decoration: underline;
    }
  }

  .szw-card__pill {
    display: inline-block;
    margin-top: 4px;
    background: $szw-surface-low;
    color: $szw-text-secondary;
    font-size: 11px;
    padding: 2px 9px;
    border-radius: 999px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .szw-card__desc {
    font-size: 12px;
    color: $szw-text-secondary;
    margin: 10px 0 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .szw-card__details-btn {
    width: 100%;
    text-align: left;
    margin-top: 12px;
    padding: 0;
    border: none;
    background: none;
    font-size: 13px;
    color: $szw-navy;
    font-family: inherit;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
  }

  .szw-card__chevron {
    font-size: 18px;
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(180deg);
    }
  }

  .szw-card__details {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 0.5px solid $szw-border;
  }

  .szw-card__inputs {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
  }

  .szw-card__input {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-family: inherit;
    padding: 7px 10px;
    border-radius: 8px;
    border: 0.5px solid $szw-border;
    background: #fff;
    color: $szw-text;
    outline: none;
    transition: border-color 0.15s;

    &:focus {
      border-color: $szw-navy;
    }

    &--sm {
      font-size: 12px;
      padding: 5px 8px;
    }
  }

  .szw-card__employees-row {
    width: 100%;
    font-size: 13px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: $szw-text-secondary;
    padding: 6px 0 2px;
    border: none;
    background: none;
    font-family: inherit;
    cursor: pointer;

    &:hover {
      color: $szw-text;
    }
  }

  .szw-card__employees-icon {
    font-size: 15px;
    color: $szw-navy;
    opacity: 0.6;
  }

  .szw-card__employees-count {
    background: $szw-surface-low;
    border-radius: 9999px;
    padding: 1px 7px;
    font-size: 11px;
    color: $szw-text-secondary;
  }

  .szw-card__contributors {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .szw-card__contributors-empty {
    font-size: 12px;
    color: $szw-text-tertiary;
    font-style: italic;
    margin: 0;
    padding: 2px 0;
  }

  .szw-card__contributor {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    padding: 8px;
    background: $szw-surface-low;
    border-radius: 8px;

    &--skeleton {
      pointer-events: none;
    }
  }

  .szw-card__contributor-avatar {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #dde1ff;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .szw-card__contributor-avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .szw-card__contributor-body {
    flex: 1;
    min-width: 0;
  }

  .szw-card__contributor-name {
    font-size: 12px;
    font-weight: 700;
    color: $szw-text;
    margin: 0 0 5px;
    line-height: 1.3;
  }

  @keyframes shimmer-anim {
    0% {
      background-position: -400px 0;
    }
    100% {
      background-position: 400px 0;
    }
  }

  .shimmer-contributor {
    background: linear-gradient(90deg, #e0e2e8 25%, #edeef2 50%, #e0e2e8 75%);
    background-size: 800px 100%;
    animation: shimmer-anim 1.4s infinite linear;
  }
</style>

<style lang="scss">
  // ── 3-dots dropdown menu (unscoped — rendered via Teleport outside component root) ──
  .szw-menu {
    position: fixed;
    z-index: 9999;
    background: #fff;
    border: 1px solid #e2e4ea;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(7, 28, 91, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06);
    min-width: 180px;
    overflow: hidden;
    padding: 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-family: 'Plus Jakarta Sans', Inter, sans-serif;
  }

  .szw-menu__item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    text-align: left;
    padding: 8px 10px;
    border: none;
    background: transparent;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.85rem;
    font-weight: 500;
    color: #191c1d;
    font-family: inherit;
    transition: background 0.12s;

    .material-symbols-outlined {
      font-size: 17px;
      color: #5f5e5a;
    }

    &:hover {
      background: #f3f4f8;
    }

    &--danger {
      color: #ba1a1a;

      .material-symbols-outlined {
        color: #ba1a1a;
      }

      &:hover {
        background: #ffdad6;
      }
    }
  }

  // ── Transfer/copy popup (unscoped — rendered via Teleport outside component root) ──
  .szw-popup {
    position: fixed;
    z-index: 9999;
    background: white;
    border: 1px solid #e2e4ea;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(7, 28, 91, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06);
    min-width: 220px;
    overflow: hidden;
    font-family: 'Plus Jakarta Sans', Inter, sans-serif;
  }

  .szw-popup__header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 16px 12px;
    border-bottom: 1px solid #f0f1f5;
    background: #f8f9ff;
  }

  .szw-popup__header-icon {
    font-size: 16px;
    color: #071c5b;
    opacity: 0.7;
  }

  .szw-popup__header-label {
    font-size: 0.78rem;
    font-weight: 700;
    color: #071c5b;
    letter-spacing: -0.01em;
  }

  .szw-popup__options {
    padding: 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .szw-popup__section-label {
    font-size: 0.68rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 6px 10px 4px;
    margin: 0;
  }

  .szw-popup__divider {
    border: none;
    border-top: 1px solid #f0f1f5;
    margin: 4px 0;
  }

  .szw-popup__option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 10px;
    border: none;
    background: transparent;
    cursor: pointer;
    width: 100%;
    text-align: left;
    transition: background 0.12s;

    &:hover {
      background: #f3f4f8;
    }

    &:active {
      background: #e8eaf2;
    }
  }

  .szw-popup__option-name {
    font-size: 0.85rem;
    font-weight: 600;
    color: #191c1d;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .szw-popup__copy-icon {
    font-size: 14px;
    color: #94a3b8;
    flex-shrink: 0;
  }

  .szw-popup__empty {
    font-size: 0.8rem;
    color: #94a3b8;
    font-style: italic;
    text-align: center;
    padding: 12px 16px;
    margin: 0;
  }
</style>
